/* Confirms a payment after the Stripe redirect returns. Polls appointment status
   until the webhook flips it to "confirmed", with a 30s timeout. */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { api } from "../../lib/api";

const POLL_MS = 2000;
const TIMEOUT_MS = 30000;

export default function StripeConfirm() {
  const { id: appointmentId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("polling"); // polling | confirmed | timeout | error
  const [appt, setAppt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let elapsed = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/patient/appointments/${appointmentId}`);
        if (cancelled) return;
        setAppt(data);
        if (data.status === "confirmed") {
          setState("confirmed");
          return;
        }
        elapsed += POLL_MS;
        if (elapsed >= TIMEOUT_MS) {
          setState("timeout");
          return;
        }
        setTimeout(tick, POLL_MS);
      } catch {
        if (!cancelled) setState("error");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [appointmentId]);

  return (
    <div className="max-w-xl mx-auto py-12 space-y-5" data-testid="stripe-confirm-page">
      {state === "polling" && (
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-teal-300 mx-auto" />
          <h2 className="text-xl font-light">Confirming with the hospital…</h2>
          <p className="text-sm text-mb-fg2">Hold tight — we're verifying the payment with Stripe.</p>
        </div>
      )}
      {state === "confirmed" && (
        <div className="text-center space-y-5" data-testid="stripe-confirm-success">
          <div className="h-12 w-12 rounded-full bg-teal-500/15 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-7 w-7 text-teal-300" />
          </div>
          <h2 className="text-2xl font-light">Appointment confirmed</h2>
          {appt?.doctor && (
            <p className="text-sm text-mb-fg2">
              {appt.doctor.full_name} · {appt.slot_start}
            </p>
          )}
          <Button onClick={() => navigate("/patient/appointments")}
            data-testid="confirm-view-appts"
            className="bg-teal-500 hover:bg-teal-400 text-zinc-950">
            View my appointments
          </Button>
        </div>
      )}
      {state === "timeout" && (
        <div className="text-center space-y-4" data-testid="stripe-confirm-timeout">
          <AlertTriangle className="h-8 w-8 text-amber-300 mx-auto" />
          <h2 className="text-xl font-light">Still processing</h2>
          <p className="text-sm text-mb-fg2">Stripe accepted the payment but the hospital hasn't confirmed yet. We'll keep watching — refresh this page in a moment.</p>
          <Button onClick={() => navigate("/patient/appointments")}
            className="bg-transparent border border-mb-border hover:bg-mb-surface2 text-white">
            View my appointments
          </Button>
        </div>
      )}
      {state === "error" && (
        <div className="text-sm text-red-400 text-center" data-testid="stripe-confirm-error">
          Couldn't load the appointment. <Button variant="link" onClick={() => navigate("/patient/appointments")}>Go back</Button>
        </div>
      )}
    </div>
  );
}
